import React, { useEffect, useState } from "react";
import { Button, TextField, Modal, Box, IconButton } from "@mui/material";
import "react-datepicker/dist/react-datepicker.css";
import "react-toastify/dist/ReactToastify.css";
import CloseIcon from "@mui/icons-material/Close";
import DatePickerWithTimeComponent from "../DatePickerWithTimeComponent";
import { useAuth } from "../../contexts/AuthContext";
import { db } from "../../firebase";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
  deleteObject,
} from "firebase/storage";
import UploaderDocForNote from "../Updloader/UploaderDocForNote";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import MovieCreationIcon from "@mui/icons-material/MovieCreation";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteIcon from "@mui/icons-material/Delete";
import "./EditTherapyNoteRedesign.css";

function EditTherapyNote(props) {
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [therapyNote, setTherapyNote] = useState("");
  const [therapyNoteDateTime, setTherapyNoteDateTime] = useState(new Date());
  const [existingFileUrls, setExistingFileUrls] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleTherapyNoteChange = (event) => {
    setTherapyNote(event.target.value);
  };

  const handleDateTimeChange = (event) => {
    setTherapyNoteDateTime(event);
  };

  const getFileIcon = (name = "") => {
    const ext = name.split(".").pop()?.toLowerCase();

    if (["pdf"].includes(ext)) {
      return <PictureAsPdfIcon sx={{ color: "#d32f2f" }} />;
    }

    if (["png", "jpg", "jpeg", "gif", "bmp", "webp"].includes(ext)) {
      return <ImageIcon sx={{ color: "#1976d2" }} />;
    }

    if (["mp3", "wav", "aac", "ogg", "m4a"].includes(ext)) {
      return <AudiotrackIcon sx={{ color: "#8e24aa" }} />;
    }

    if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext)) {
      return <MovieCreationIcon sx={{ color: "#6d4c41" }} />;
    }

    if (["zip", "rar", "7z"].includes(ext)) {
      return <FolderZipIcon sx={{ color: "#f57c00" }} />;
    }

    if (["doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv"].includes(ext)) {
      return <DescriptionIcon sx={{ color: "#2e7d32" }} />;
    }

    return <InsertDriveFileIcon sx={{ color: "#607d8b" }} />;
  };

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const filtered = selectedFiles.filter((f) => f.size <= 50 * 1024 * 1024);
    const rejected = selectedFiles.filter((f) => f.size > 50 * 1024 * 1024);

    if (rejected.length) {
      toast.error("Some files exceed 50MB and were skipped.");
    }

    setNewFiles((prev) => [...prev, ...filtered]);
  };

  const getStorageRefFromUrl = (url) => {
    try {
      const storage = getStorage();
      const path = decodeURIComponent(url.split("/o/")[1].split("?")[0]);
      return ref(storage, path);
    } catch {
      return null;
    }
  };

  const uploadFilesAndGetUrls = async (noteId) => {
    if (!newFiles.length) return [];

    const storage = getStorage();
    const fileUrls = [];

    for (let i = 0; i < newFiles.length; i++) {
      const file = newFiles[i];
      const prefix = props.clientId
        ? `clients/${props.clientId}`
        : "therapyNotes";
      const filePath = `Documents/${prefix}/${noteId}/${file.name}`;
      const storageRef = ref(storage, filePath);
      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);
      fileUrls.push({ url: fileUrl, name: file.name });
    }

    return fileUrls;
  };

  const handleEditTherapyNote = async (note, noteId, effectiveDateTime) => {
    const noteData = {
      noteContent: note,
      updateAt: serverTimestamp(),
      effectiveDateTime: effectiveDateTime,
      doneBy: currentUser.displayName,
    };

    if (therapyNote && therapyNoteDateTime) {
      setIsLoading(true);
      try {
        const noteRef = doc(db, "notes", noteId);
        const uploaded = await uploadFilesAndGetUrls(noteId);
        const mergedFiles =
          uploaded.length || existingFileUrls.length
            ? [...(existingFileUrls || []), ...uploaded]
            : undefined;

        await updateDoc(noteRef, {
          ...noteData,
          ...(mergedFiles ? { fileUrls: mergedFiles } : {}),
        });

        setExistingFileUrls(mergedFiles || []);
        setNewFiles([]);
        toast.success("Therapy note successfully updated!", {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
        });
        setIsLoading(false);
        props.closemodal();
      } catch (error) {
        console.log("error updating note :", error);
        toast.error("Problem when updating note", {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
        });
      }
    } else {
      toast.error("Some mandatory fields are empty. Please check.", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
    }
  };

  const handleSubmit = () => {
    handleEditTherapyNote(therapyNote, props.noteId, therapyNoteDateTime);
  };

  const confirmDeleteFile = (file) => {
    setFileToDelete(file);
    setShowDeleteConfirm(true);
  };

  const handleDeleteFile = async () => {
    if (!fileToDelete) return;

    const updated = existingFileUrls.filter((f) => f.url !== fileToDelete.url);

    try {
      const noteRef = doc(db, "notes", props.noteId);
      await updateDoc(noteRef, { fileUrls: updated });
      setExistingFileUrls(updated);

      try {
        const refFrom = getStorageRefFromUrl(fileToDelete.url);
        if (refFrom) await deleteObject(refFrom);
      } catch (e) {
        console.warn("Storage delete failed:", e);
      }

      toast.success("Attachment removed");
    } catch (e) {
      toast.error("Error removing attachment");
    } finally {
      setShowDeleteConfirm(false);
      setFileToDelete(null);
    }
  };

  useEffect(() => {
    if (props.note || props.noteContent) {
      setTherapyNote(props.note || props.noteContent || "");
    }

    if (props.effectiveDate || props.effectiveDateTime) {
      const d = props.effectiveDate || props.effectiveDateTime;
      const dateObj = d?.toDate ? d.toDate() : d;
      if (dateObj) setTherapyNoteDateTime(new Date(dateObj));
    }

    if (props.fileUrls?.length) {
      setExistingFileUrls(props.fileUrls);
    }
  }, [
    props.note,
    props.noteContent,
    props.effectiveDate,
    props.effectiveDateTime,
    props.fileUrls,
  ]);

  return (
    <div className="edit-therapy-note-modal">
      <div className="edit-therapy-note-header">
        <div>
          <h2>Edit therapy note</h2>
          <p>Update the therapy note, effective date, and attachments.</p>
        </div>
        <button
          type="button"
          className="edit-therapy-note-close"
          onClick={props.closemodal}
          aria-label="Close edit therapy note modal"
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>

      <div className="edit-therapy-note-body">
        <section className="edit-therapy-note-section">
          <div className="edit-therapy-note-section-title">
            <span>Note details</span>
          </div>

          <div className="edit-therapy-note-textarea">
            <div className="edit-therapy-note-label">
              Note content <span>*</span>
            </div>
            <TextField
              value={therapyNote}
              onChange={handleTherapyNoteChange}
              placeholder="Type 1 to 1 therapy session note here"
              multiline
              rows={10}
              fullWidth
            />
          </div>

          <div className="edit-therapy-note-date-row">
            <DatePickerWithTimeComponent
              title="Effective date and time"
              value={therapyNoteDateTime}
              onChange={handleDateTimeChange}
            />
          </div>
        </section>

        <section className="edit-therapy-note-section">
          <div className="edit-therapy-note-section-title">
            <span>Attachments</span>
            <strong>{existingFileUrls.length + newFiles.length} files</strong>
          </div>

          {existingFileUrls?.length ? (
            <div className="edit-therapy-note-file-grid">
              {existingFileUrls.map((f, idx) => (
                <div className="edit-therapy-note-file" key={idx}>
                  <a href={f.url} target="_blank" rel="noreferrer">
                    {getFileIcon(f.name)}
                    <span>{f.name || "File"}</span>
                  </a>
                  <IconButton
                    size="small"
                    aria-label="Delete attachment"
                    onClick={() => confirmDeleteFile(f)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>
              ))}
            </div>
          ) : (
            <div className="edit-therapy-note-empty-state">
              No existing attachments.
            </div>
          )}

          {newFiles?.length ? (
            <div className="edit-therapy-note-file-grid">
              {newFiles.map((f, idx) => (
                <div className="edit-therapy-note-file pending" key={idx}>
                  {getFileIcon(f.name)}
                  <span>{f.name}</span>
                </div>
              ))}
            </div>
          ) : null}

          <div className="edit-therapy-note-uploader">
            <UploaderDocForNote
              handleFileChange={handleFileChange}
              files={newFiles}
            />
            <p>Max file size: 50 MB each.</p>
          </div>
        </section>
      </div>

      <div className="edit-therapy-note-footer">
        <Button
          variant="outlined"
          className="edit-therapy-note-cancel"
          onClick={props.closemodal}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          className="edit-therapy-note-save"
          disabled={isLoading}
          onClick={handleSubmit}
        >
          {isLoading ? "Updating..." : "Update note"}
        </Button>
      </div>

      <Modal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <Box className="edit-therapy-note-delete-box">
          <div className="edit-therapy-note-delete-title">
            Remove this attachment?
          </div>
          <div className="edit-therapy-note-delete-file">
            {fileToDelete?.name}
          </div>
          <div className="edit-therapy-note-delete-actions">
            <Button
              variant="outlined"
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={handleDeleteFile}>
              Delete
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
}

export default EditTherapyNote;
